import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useUserSession } from '@vertesia/ui/session';
import { Button, Spinner } from '../../core/index.js';
import { useUITranslation } from '../../i18n/index.js';
import { CheckCircle2, ExternalLink, ShieldAlertIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useOAuthPopup } from './useOAuthPopup.js';
/**
 * Flexible connect button for remote MCP tool collections.
 * - 'compact': minimal size for lists (no disconnect)
 * - 'default': standard button size (no disconnect)
 * - 'full': with label, status, and disconnect button
 */
export function RemoteMcpConnectionButton({ appId, collectionId, collectionName, authenticated: providedAuthenticated, onAuthChange, onError, variant = 'default', showLabel = false, showDisconnect = false, readOnly = false }) {
    const { client } = useUserSession();
    const { t } = useUITranslation();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(providedAuthenticated === undefined);
    const [authenticating, setAuthenticating] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const authenticated = providedAuthenticated ?? status?.authenticated ?? false;
    const displayName = collectionName ?? collectionId;
    const loadStatus = useCallback(async () => {
        if (providedAuthenticated !== undefined) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await client.remoteMcpConnections.getCollectionStatus(appId, collectionId);
            setStatus(data);
        }
        catch (error) {
            console.error('[RemoteMcpConnectionButton] Failed to load OAuth status:', error);
        }
        finally {
            setLoading(false);
        }
    }, [client, appId, collectionId, providedAuthenticated]);
    useEffect(() => {
        loadStatus();
    }, [loadStatus]);
    const { openOAuthPopup } = useOAuthPopup({
        onComplete: () => {
            setAuthenticating(false);
            loadStatus();
            onAuthChange?.();
        },
        onError: (error) => {
            console.error('OAuth failed:', error);
            setAuthenticating(false);
        }
    });
    const handleConnect = async () => {
        try {
            setAuthenticating(true);
            onError?.(null);
            const response = await client.remoteMcpConnections.authorize(appId, collectionId);
            if (response.connected) {
                setAuthenticating(false);
                await loadStatus();
                onAuthChange?.();
            }
            else if (response.authorization_url) {
                openOAuthPopup(response.authorization_url);
            }
            else {
                onError?.(`${displayName}: Authorization URL not provided by server`);
                setAuthenticating(false);
            }
        }
        catch (error) {
            console.error('Failed to authorize:', error);
            const raw = error instanceof Error
                ? (error.original_message ?? error.message)
                : 'Failed to connect';
            const detail = raw.replace(/^[A-Za-z\s]+:\s/, '');
            onError?.(`${displayName}: ${detail}`);
            setAuthenticating(false);
        }
    };
    const handleDisconnect = async () => {
        try {
            setDisconnecting(true);
            await client.remoteMcpConnections.disconnect(appId, collectionId);
            await loadStatus();
            onAuthChange?.();
        }
        catch (error) {
            console.error('Failed to disconnect:', error);
        }
        finally {
            setDisconnecting(false);
        }
    };
    if (loading) {
        return (_jsx("div", { className: "flex items-center gap-2 text-sm", children: _jsx(Spinner, { className: "size-4" }) }));
    }
    if (variant === 'full') {
        if (!authenticated) {
            return (_jsxs("div", { className: "flex items-center gap-2 text-sm", children: [showLabel && _jsxs("span", { className: "font-medium text-foreground", children: [displayName, ":"] }), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleConnect, disabled: authenticating, children: authenticating ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { className: "size-4" }), _jsx("span", { children: t('mcpOAuth.authenticating') })] })) : (_jsxs(_Fragment, { children: [_jsx(ShieldAlertIcon, { className: "size-4" }), _jsx("span", { children: t('mcpOAuth.authenticate') })] })) })] }));
        }
        return (_jsxs("div", { className: "flex items-center gap-2", children: [showLabel && _jsxs("span", { className: "font-medium text-sm text-foreground", children: [displayName, ":"] }), _jsxs("div", { className: "flex items-center gap-1 text-success text-sm", children: [_jsx(CheckCircle2, { className: "size-4" }), _jsx("span", { children: t('mcpOAuth.connected') })] }), showDisconnect && (_jsx(Button, { variant: "ghost", size: "sm", onClick: handleDisconnect, disabled: disconnecting, children: disconnecting ? _jsx(Spinner, { className: "size-4" }) : t('mcpOAuth.disconnect') }))] }));
    }
    if (authenticated) {
        if (variant === 'compact') {
            return (_jsxs("div", { className: "flex items-center gap-2", children: [showLabel && _jsxs("span", { className: "font-medium text-xs text-foreground", children: [displayName, ":"] }), _jsxs("div", { className: "flex items-center gap-1 text-success", children: [_jsx(CheckCircle2, { className: "size-3" }), _jsx("span", { className: "text-xs", children: t('mcpOAuth.connected') })] }), !readOnly && showDisconnect && (_jsx(Button, { variant: "ghost", size: "sm", onClick: handleDisconnect, disabled: disconnecting, className: "h-6 px-2 text-xs", children: disconnecting ? _jsx(Spinner, { className: "size-3" }) : t('mcpOAuth.disconnect') }))] }));
        }
        return (_jsxs("div", { className: "flex items-center gap-1 text-success", children: [_jsx(CheckCircle2, { className: "size-4" }), _jsx("span", { className: "text-sm", children: t('mcpOAuth.connected') })] }));
    }
    if (variant === 'compact') {
        if (readOnly) {
            return (_jsxs("div", { className: "flex items-center gap-2", children: [showLabel && _jsxs("span", { className: "font-medium text-xs text-foreground", children: [collectionName, ":"] }), _jsx("div", { className: "flex items-center gap-1 text-muted", children: _jsx("span", { className: "text-xs", children: t('mcpOAuth.notConnected') }) })] }));
        }
        return (_jsxs("div", { className: "flex items-center gap-2", children: [showLabel && _jsxs("span", { className: "font-medium text-xs text-foreground", children: [displayName, ":"] }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleConnect, disabled: authenticating, className: "h-6 px-2 text-xs", children: authenticating ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { className: "size-3" }), _jsx("span", { children: t('mcpOAuth.connecting') })] })) : (_jsxs(_Fragment, { children: [_jsx(ExternalLink, { className: "size-3 mr-1" }), _jsx("span", { children: t('mcpOAuth.connect') })] })) })] }));
    }
    return (_jsx(Button, { variant: "outline", size: "sm", onClick: handleConnect, disabled: authenticating, children: authenticating ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { className: "size-4" }), _jsx("span", { children: t('mcpOAuth.connecting') })] })) : (_jsxs(_Fragment, { children: [_jsx(ExternalLink, { className: "size-4 mr-1" }), _jsx("span", { children: t('mcpOAuth.connect') })] })) }));
}
//# sourceMappingURL=RemoteMcpConnectionButton.js.map